// The game page's chart, drawn as SVG: the line (or daily and weekly bands),
// its axes, and what's laid over it (chart-overlays.ts places those in time;
// this maps them to pixels), with its cursor and tooltip, the menu a marker
// covering several updates opens, and the counts on the toggles under it.
// The arithmetic is chart.ts's.

import type { HistoryResponse, SaleMark, UpdateMark } from "../../../shared/api.ts";
import { eveningSpans, placeUpdates, saleAt, saleSpans, salesIn } from "./chart-overlays.ts";
import {
  type Point,
  UNITS,
  bucketLabel,
  compact,
  high,
  linearScale,
  low,
  mid,
  nearestByTime,
  placeBeside,
  segmentsOf,
  timeTicks
} from "./chart.ts";
import { element, eventTarget } from "../dom.ts";
import { number } from "../figures.ts";
import { postUrl } from "../routes.ts";
import { discountText, dollars } from "../store-facts.ts";
import { pageEvenings, previewEvenings, previewKind, shownEvenings, shownKinds, shownSales } from "../toggles.ts";
import { UPDATE_KINDS, updateLabel, updateTitle } from "../updates.ts";
import { escapeHtml } from "../web.ts";

// A game page's chart, by its element: the series drawn on it (null before
// one is, or after a failed load), the width it was drawn at, and how to
// redraw its evenings alone.
interface ChartState {
  series: HistoryResponse | null;
  renderedWidth: number;
  drawEvenings: () => void;
}

const charts = new WeakMap<HTMLElement, ChartState>();

// The chart in `el`'s state, a blank one until it's drawn.
export function chartOf(el: HTMLElement): ChartState {
  let state = charts.get(el);
  if (!state) {
    state = { series: null, renderedWidth: 0, drawEvenings: () => {} };
    charts.set(el, state);
  }
  return state;
}

// One update as a kind label above its title, in the tooltip and the menu.
const updateRow = (update: UpdateMark): string => `
  <div class="update-kind ${update.kind}">${updateLabel(update.kind)}</div>
  <div>${escapeHtml(updateTitle(update))}</div>`;

// The open pick-one menu, if any (see openMenu), as { menu, marker }. Clicking
// or tapping anywhere else closes it, and so does Escape. Markers are
// re-rendered often, so these listeners live here rather than per chart.
let openedMenu: { menu: HTMLElement; marker: SVGElement } | null = null;

document.addEventListener("pointerdown", (event) => {
  if (openedMenu && !eventTarget(event, ".update-menu, .update.multi")) {
    closeUpdateMenu();
  }
});

document.addEventListener("keydown", (event) => {
  if (openedMenu && event.key === "Escape") {
    const { marker } = openedMenu;
    closeUpdateMenu();
    marker.focus();
  }
});

export function closeUpdateMenu(): void {
  if (!openedMenu) return;

  openedMenu.menu.hidden = true;
  openedMenu.marker.setAttribute("aria-expanded", "false");
  openedMenu = null;
}

// Sets the update toggles' counts to what the chart in `el` covers.
function showUpdateCounts(el: Element, counts: Readonly<Record<string, number>>): void {
  for (const button of el.parentElement?.querySelectorAll<HTMLElement>("[data-update-kind]") ?? []) {
    const kind = button.dataset.updateKind ?? "";
    showCount(button, `${updateLabel(kind)}s`, counts[kind] ?? 0);
  }
}

// Sets the sales toggle's count to how many sales the chart in `el` covers.
function showSaleCount(el: Element, count: number): void {
  const button = el.parentElement?.querySelector<HTMLElement>("[data-sales]");
  if (button) showCount(button, "Sales", count);
}

// A chart toggle's count, shown and said: "Sales, 2 in range".
function showCount(button: HTMLElement, label: string, count: number): void {
  element(".count", HTMLElement, button).textContent = String(count);
  button.setAttribute("aria-label", `${label}, ${count} in range`);
}

// Shows `html` in the chart `el` in place of a chart ("No data in this range
// yet.", or why it didn't load): nothing to redraw, no evenings to shade, and
// nothing counted on the toggles.
export function chartStatus(el: HTMLElement, html: string): void {
  const drawn = chartOf(el);
  drawn.series = null;
  drawn.drawEvenings = () => {};
  el.innerHTML = html;
  showUpdateCounts(el, {});
  showSaleCount(el, 0);
}

// A sale as the tooltip and a band's label say it: "On sale −50%, $29.99".
const saleText = (sale: SaleMark): string =>
  `On sale ${discountText(sale.discount)}, ${dollars(sale.final)}`;

export function renderChart(el: HTMLElement, series: HistoryResponse): void {
  openedMenu = null; // whatever was open is about to be replaced

  const { resolution } = series;
  const points: readonly Point[] = series.points;
  const first = points[0];
  const last = points.at(-1);

  if (!first || !last) {
    chartStatus(el, `<div class="status">No data in this range yet.</div>`);
    return;
  }

  const drawn = chartOf(el);
  drawn.series = series;
  drawn.renderedWidth = el.clientWidth;

  // Daily and weekly points are drawn alike, as an average in a min–max band.
  const banded = resolution !== "hour";
  const unit = UNITS[resolution];

  const width = Math.max(el.clientWidth, 260);
  const height = 300;
  const pad = { top: 16, right: 16, bottom: 28, left: 48 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;

  const t0 = first.t;
  const t1 = last.t;
  const max = Math.max(...points.map(high));
  const { step, yMax } = linearScale(max);

  const x = (t: number): number => pad.left + (t1 === t0 ? plotW / 2 : ((t - t0) / (t1 - t0)) * plotW);
  const y = (v: number): number => pad.top + plotH - (v / yMax) * plotH;
  const baseline = y(0);
  const xy = (t: number, v: number): string => `${x(t).toFixed(1)},${y(v).toFixed(1)}`;

  // Split at missing hours/days so gaps show as breaks, not straight lines.
  const segments = segmentsOf(points, unit);

  const path = (seg: readonly Point[], f: (p: Point) => number): string =>
    seg.map((p, i) => `${i ? "L" : "M"}${xy(p.t, f(p))}`).join("");

  // The point closest to px: what the cursor snaps to, by the same rule an
  // update's tooltip point is picked by (chart-overlays.ts's placeUpdates).
  const nearestPoint = (px: number): Point =>
    nearestByTime(points, t0 + ((px - pad.left) / plotW) * (t1 - t0)) ?? first;

  // Each shown update is marked at the hour/day it falls in, and listed in
  // the tooltip of the nearest point; hidden kinds' buckets are kept for
  // their preview markers (chart-overlays.ts's placeUpdates).
  const { marks, byPoint, hidden, counts } = placeUpdates(series.updates ?? [], points, resolution, shownKinds);

  // A marker's line and diamond at x `ux`.
  const markShape = (ux: string): string => `
    <line x1="${ux}" x2="${ux}" y1="${pad.top}" y2="${baseline}"/>
    <path d="M${ux},${pad.top - 12}l4,4l-4,4l-4,-4Z"/>`;

  // Each hidden kind's markers, faded, in a layer of their own that's shown
  // only while its toggle is hovered (render-game.ts's showPreviewMarks): a
  // preview of turning it on without redrawing the chart. Not links; nothing
  // to click.
  let previewMarks = "";

  for (const [kind, ts] of hidden) {
    const shapes = ts
      .map((t) => `<g class="update ${kind} preview">${markShape(x(t).toFixed(1))}</g>`)
      .join("");

    previewMarks += `<g class="preview-marks" data-kind="${kind}"
      ${kind === previewKind ? "" : "hidden"}>${shapes}</g>`;
  }

  // A marker opens its Steam post; one covering several updates opens a menu
  // to pick which. The wide transparent `hit` line makes them easy to click;
  // the shapes drawn over them don't take pointer events (see style.css).
  let updateMarks = "";

  for (const [t, updates] of marks) {
    const kind = UPDATE_KINDS.find((k) =>
      updates.some((u) => u.kind === k)
    );
    const ux = x(t).toFixed(1);
    const body = `${markShape(ux)}
      <line class="hit" x1="${ux}" x2="${ux}" y1="${pad.top - 12}" y2="${baseline}"/>`;
    const [only] = updates;

    updateMarks +=
      updates.length === 1 && only
        ? `<a class="update ${kind}" target="_blank" rel="noopener"
             href="${escapeHtml(postUrl(series.app_id, only))}"
             aria-label="${escapeHtml(
               `${updateLabel(only.kind)}: ${updateTitle(only)}`
             )} on Steam">${body}</a>`
        : `<g class="update ${kind} multi" role="button" tabindex="0"
             aria-haspopup="menu" aria-expanded="false" data-mark="${t}"
             aria-label="${updates.length} updates, pick one to open on Steam"
             >${body}</g>`;
  }

  // Sales as bands behind the line (chart-overlays.ts's saleSpans), at
  // least 2px wide, so a sale begun after the last point still shows as a
  // sliver ending there.
  const sales = salesIn(series.sales, t0, t1, unit);
  let saleBands = "";

  if (shownSales.has("sales")) {
    for (const { sale, from, to } of saleSpans(sales, t0, t1)) {
      const ex = x(to);
      const bw = Math.max(ex - x(from), 2);
      const bx = ex - bw;

      saleBands += `<rect class="sale-band" x="${bx.toFixed(1)}" y="${pad.top}"
          width="${bw.toFixed(1)}" height="${(baseline - pad.top).toFixed(1)}"/>
        <rect class="sale-bar" x="${bx.toFixed(1)}" y="${pad.top}"
          width="${bw.toFixed(1)}" height="3"/>`;
      if (bw >= 40) {
        saleBands += `<text x="${(bx + 4).toFixed(1)}" y="${pad.top + 15}"
          >${discountText(sale.discount)}</text>`;
      }
    }
  }

  let shapes = "";

  for (const seg of segments) {
    const start = seg[0];
    const end = seg.at(-1);
    if (!start || !end) continue;

    if (seg.length === 1) {
      shapes += `<circle class="dot" cx="${x(start.t).toFixed(1)}" cy="${y(mid(start)).toFixed(1)}" r="2.5"/>`;
      continue;
    }

    // Daily/weekly: shaded min–max band. Hourly: shaded area under the line.
    const area = banded
      ? path(seg, high) +
        seg.slice().reverse().map((p) => `L${xy(p.t, low(p))}`).join("") +
        "Z"
      : path(seg, mid) +
        `L${x(end.t).toFixed(1)},${baseline}` +
        `L${x(start.t).toFixed(1)},${baseline}Z`;

    shapes += `<path class="area" d="${area}"/><path class="line" d="${path(seg, mid)}"/>`;
  }

  let grid = "";
  let labels = "";

  for (let v = 0; v <= yMax; v += step) {
    const yy = y(v).toFixed(1);
    grid += `<line x1="${pad.left}" x2="${width - pad.right}" y1="${yy}" y2="${yy}"/>`;
    labels += `<text x="${pad.left - 8}" y="${yy}" dy="0.32em" text-anchor="end">${compact.format(v)}</text>`;
  }

  const xAxis = timeTicks(t0, t1, resolution, plotW);
  for (const { t, anchor } of xAxis.ticks) {
    labels += `<text x="${x(t).toFixed(1)}" y="${height - 8}" text-anchor="${anchor}">${xAxis.format.format(t * 1000)}</text>`;
  }

  el.innerHTML = `
    <svg viewBox="0 0 ${width} ${height}" role="img" aria-label="Player count chart">
      <g class="grid">${grid}</g>
      <g class="evenings"></g>
      <g class="sales">${saleBands}</g>
      ${previewMarks}
      ${updateMarks}
      ${shapes}
      <g class="axis">${labels}</g>
      <g class="cursor" visibility="hidden">
        <line y1="${pad.top}" y2="${baseline}"/>
        <circle r="4"/>
      </g>
    </svg>
    <div class="tooltip" hidden></div>
    <div class="update-menu" role="menu" hidden></div>`;

  showUpdateCounts(el, counts);
  showSaleCount(el, sales.length);

  // Each shown region's evenings (toggled, or previewed from an off toggle
  // or the play-time insight) as bands behind the line, in that region's own
  // time (chart-overlays.ts's eveningSpans); only on hourly points. A band
  // wide enough is labelled; on the longer ranges the toggles are the legend.
  const evenings = element(".evenings", SVGGElement, el);

  drawn.drawEvenings = () => {
    const regions = new Set([...shownEvenings, ...pageEvenings, ...previewEvenings]);
    let bands = "";

    if (resolution === "hour") {
      for (const { region, label, from, to } of eveningSpans(regions, t0, t1)) {
        const bx = x(from);
        const bw = x(to) - bx;

        bands += `<rect class="evening ${region}" x="${bx.toFixed(1)}" y="${pad.top}"
          width="${bw.toFixed(1)}" height="${(baseline - pad.top).toFixed(1)}"/>`;
        if (bw >= 64) {
          bands += `<text x="${(bx + bw / 2).toFixed(1)}" y="${pad.top + 12}"
            text-anchor="middle">${label}</text>`;
        }
      }
    }

    evenings.innerHTML = bands;
  };

  drawn.drawEvenings();

  const svg = element("svg", SVGSVGElement, el);
  const cursor = element(".cursor", SVGGElement, el);
  const cursorLine = element("line", SVGLineElement, cursor);
  const cursorDot = element("circle", SVGCircleElement, cursor);
  const tooltip = element(".tooltip", HTMLElement, el);
  const menu = element(".update-menu", HTMLElement, el);

  // the point the tooltip shows, so moves that stay on it skip the rebuild
  let shown: Point | null = null;

  // Lists a marker's updates beside it, each a link to its own Steam post.
  function openMenu(marker: SVGElement): void {
    const t = Number(marker.dataset.mark);
    const box = svg.getBoundingClientRect();

    menu.innerHTML = (marks.get(t) ?? [])
      .map(
        (u) => `
          <a role="menuitem" target="_blank" rel="noopener"
             href="${escapeHtml(postUrl(series.app_id, u))}">${updateRow(u)}</a>`
      )
      .join("");

    closeUpdateMenu();
    menu.hidden = false;
    marker.setAttribute("aria-expanded", "true");
    openedMenu = { menu, marker };

    placeBeside(menu, x(t) * (box.width / width), box.width);
    tooltip.hidden = true;
    shown = null; // so the tooltip comes back on this point once the menu closes
    menu.querySelector<HTMLAnchorElement>("a")?.focus();
  }

  svg.addEventListener("click", (event) => {
    const marker = eventTarget<SVGGElement>(event, ".update.multi");
    if (marker) openMenu(marker);
  });

  // <g> isn't a button, so Enter/Space don't activate it on their own.
  svg.addEventListener("keydown", (event) => {
    const marker = eventTarget<SVGGElement>(event, ".update.multi");

    if (marker && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      openMenu(marker);
    }
  });

  svg.addEventListener("pointermove", (event) => {
    if (!menu.hidden) return;

    const box = svg.getBoundingClientRect();
    const scale = box.width / width;
    const px = (event.clientX - box.left) / scale;

    const nearest = nearestPoint(px);
    if (nearest === shown) return;
    shown = nearest;

    const cx = String(x(nearest.t));

    cursorLine.setAttribute("x1", cx);
    cursorLine.setAttribute("x2", cx);
    cursorDot.setAttribute("cx", cx);
    cursorDot.setAttribute("cy", String(y(mid(nearest))));
    cursor.setAttribute("visibility", "visible");

    const sale = shownSales.has("sales") ? saleAt(sales, nearest.t, unit) : undefined;
    const updateLines =
      (sale ? `<div class="tip-sale">${saleText(sale)}</div>` : "") +
      (byPoint.get(nearest.t) ?? [])
        .map((u) => `<div class="tip-update">${updateRow(u)}</div>`)
        .join("");

    tooltip.innerHTML =
      ("avg" in nearest
        ? `<div class="muted">${bucketLabel(nearest.t, resolution)}</div>
           <strong>${number.format(nearest.avg)}</strong> avg
           <div class="muted">${number.format(nearest.min)} – ${number.format(nearest.max)}</div>`
        : `<div class="muted">${bucketLabel(nearest.t, resolution)}</div>
           <strong>${number.format(nearest.player_count)}</strong> players`) + updateLines;
    tooltip.hidden = false;

    placeBeside(tooltip, Number(cx) * scale, box.width);
  });

  svg.addEventListener("pointerleave", () => {
    shown = null;
    cursor.setAttribute("visibility", "hidden");
    tooltip.hidden = true;
  });
}
